import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Image from '@cartwhl/ui/atoms/Image';
import Select from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import {
  noticesStore,
  posingStore,
  projectStore,
  sceneSettingsStore,
} from 'stores';
import { pollProjectExportStatus } from 'stores/projectStore';

import { exportAnimationFile } from '../utils';
import ExportLoader from './ExportLoader';

export default function DownloadImage({
  handleOpenChange,
}: {
  handleOpenChange: (open: boolean) => void;
}) {
  const {
    backgroundSettings: { canvasHeight, canvasWidth },
  } = useSnapshot(sceneSettingsStore.state);
  const {
    currentProject,
    currentProjectGenerationHistory,
    export: projectExport,
  } = useSnapshot(projectStore.state);
  const { posingSystem } = useSnapshot(posingStore.state);
  const [renderedImageUrl, setRenderedImageUrl] = useState('');
  const [poseImageUrl, setPoseImageUrl] = useState('');
  const [imageType, setImageType] = useState('pose');
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  // Computed values
  const currentImageUrl =
    imageType === 'render' ? renderedImageUrl : poseImageUrl;
  const currentImageDimensions =
    imageType === 'render' && imageDimensions.width > 0
      ? `${imageDimensions.width}x${imageDimensions.height}`
      : `${canvasWidth}x${canvasHeight}`;

  // Event handlers
  const handleDownload = async () => {
    try {
      // If it's a data URL (pose image), use the existing function
      if (currentImageUrl.startsWith('data:')) {
        exportAnimationFile(
          currentImageUrl,
          'png',
          currentProject.data?.projectName || 'Pose',
        );
        return;
      }

      // If it's a URL (rendered image), fetch and download as blob
      const response = await fetch(currentImageUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      exportAnimationFile(
        blobUrl,
        'png',
        currentProject.data?.projectName || 'Pose',
      );

      // Clean up the blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Failed to download image:', error);
      noticesStore.error('Failed to download image. Please try again.');
    }
  };

  // Capture screenshot for PNG export
  useEffect(() => {
    const captureScreenshot = async () => {
      try {
        const screenshot = await posingSystem.current?.screenshotCanvas();
        if (screenshot) {
          setPoseImageUrl(`data:image/png;base64,${screenshot}`);
        }
      } catch (error) {
        console.error('Failed to capture screenshot:', error);
      }
    };
    captureScreenshot();
  }, []);

  // Check for rendered pose in generation history
  useEffect(() => {
    if (currentProjectGenerationHistory.data?.items) {
      const renderedPose = currentProjectGenerationHistory.data?.items.find(
        (generation) => !!generation.renderedPngURL,
      );

      if (renderedPose?.renderedPngURL) {
        setRenderedImageUrl(renderedPose.renderedPngURL);
        setImageType('render'); // Default to render view if available
      }
    }
  }, []);

  return (
    <div className="relative flex flex-col space-y-4">
      {projectExport.status === 'pending' ||
      projectExport.status === 'ready' ? (
        <ExportLoader
          estimatedFinishedAt={
            new Date(projectExport.data?.estimatedFinishTime || '')
          }
          exportStartedAt={new Date(projectExport.data?.startedAt || '')}
          fileDownloadPath={projectExport.data?.path || ''}
          pollExportStatus={pollProjectExportStatus}
        />
      ) : (
        <div className="bg-gray-30 aspect-3/2 overflow-hidden rounded-lg sm:aspect-video">
          <Image
            src={currentImageUrl}
            alt="Screenshot"
            crossOrigin="anonymous"
            className="h-full w-full object-contain"
            onLoad={(e) => {
              const img = e.target as HTMLImageElement;
              setImageDimensions({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-[3fr_2fr] items-start gap-x-8">
        <div className="flex flex-col gap-3">
          <Text variant="studio/body/medium/sm">Image to download</Text>
          <Select
            inModal={true}
            value={imageType}
            onValueChange={(value) => {
              setImageType(value);
            }}
            items={[
              ...(renderedImageUrl
                ? [
                    {
                      label: 'Render view image',
                      value: 'render',
                    },
                  ]
                : []),
              {
                label: 'Pose view image',
                value: 'pose',
              },
            ]}
          />
        </div>
        <div className="flex flex-col gap-3">
          <Text variant="studio/body/medium/sm">Image size</Text>
          <div className="flex h-10 items-center">
            <Text className="text-gray-60" variant="studio/body/sm">
              {currentImageDimensions}
            </Text>
          </div>
        </div>
      </div>

      <div className="border-gray-30 dark:border-gray-75 dark:bg-gray-90 sticky -bottom-4 z-10 -mx-4 flex justify-between rounded-b-2xl border-t bg-white px-4 pt-4 pb-4 sm:mx-0 sm:px-0 md:pb-0">
        <Button onClick={() => handleOpenChange(false)}>Close</Button>
        <Button
          variant="primary"
          disabled={!currentImageUrl || projectExport.status === 'pending'}
          onClick={handleDownload}
        >
          Download
        </Button>
      </div>
    </div>
  );
}
